import { useEffect, useState } from 'react';
import { AlertCircle, CalendarDays, Clock, Mail, MapPin } from 'lucide-react';
import { Button } from '@/components';
import {
  useGetComparableBerthsQuery,
  useGetMediationCaseByIdQuery,
  useProposeReallocationMutation,
  useResolveExtensionMutation,
} from '@/services/MediationService';
import { BerthMediationCase } from '@/types/berth-mediation.types';
import { formatDisplayDateTime } from '@/utils/Date';
import { enqueueSnackbar } from 'notistack';
import { getErrorMessage } from '@/utils/errorUtils';

interface BerthMediationPanelProps {
  mediationCaseId: string;
  onResolved?: () => void;
}

const BerthMediationPanel = ({
  mediationCaseId,
  onResolved,
}: BerthMediationPanelProps) => {
  const { data, refetch, isLoading } =
    useGetMediationCaseByIdQuery(mediationCaseId);
  const mediationCase = data?.data as BerthMediationCase | undefined;

  const canLoadComparable =
    mediationCase?.status === 'OPEN' ||
    mediationCase?.status === 'AWAITING_MANAGER';

  const { data: comparableData, isFetching: loadingBerths } =
    useGetComparableBerthsQuery(mediationCaseId, { skip: !canLoadComparable });

  const [proposedBerthId, setProposedBerthId] = useState('');
  const [boaterABerthId, setBoaterABerthId] = useState('');
  const [reason, setReason] = useState('');

  const [proposeReallocation, { isLoading: proposing }] =
    useProposeReallocationMutation();
  const [resolveExtension, { isLoading: resolving }] =
    useResolveExtensionMutation();

  useEffect(() => {
    setProposedBerthId('');
    setBoaterABerthId('');
    setReason('');
  }, [mediationCaseId, mediationCase?.status]);

  const statusLabel = mediationCase?.status?.replace('_', ' ') || 'UNKNOWN';
  const overlapWindow =
    mediationCase?.overlap_start && mediationCase?.overlap_end
      ? `${formatDisplayDateTime(mediationCase.overlap_start)} - ${formatDisplayDateTime(mediationCase.overlap_end)}`
      : '—';
  const originalBerthNumber =
    mediationCase?.original_berth?.berth_number ||
    mediationCase?.conflicting_booking?.berth?.berth_number ||
    mediationCase?.requesting_booking?.berth?.berth_number ||
    '—';
  const requestingBoaterName =
    mediationCase?.requesting_booking?.boater_name ||
    mediationCase?.requesting_booking?.user?.name ||
    mediationCase?.requesting_booking?.boater_email ||
    mediationCase?.requesting_booking?.user?.email ||
    'Extension requester';
  const conflictingBoaterName =
    mediationCase?.conflicting_booking?.boater_name ||
    mediationCase?.conflicting_booking?.user?.name ||
    mediationCase?.conflicting_booking?.boater_email ||
    mediationCase?.conflicting_booking?.user?.email ||
    'Conflicting booking holder';
  const requestingBoaterEmail =
    mediationCase?.requesting_booking?.boater_email ||
    mediationCase?.requesting_booking?.user?.email;
  const conflictingBoaterEmail =
    mediationCase?.conflicting_booking?.boater_email ||
    mediationCase?.conflicting_booking?.user?.email;
  const requestingBerthNumber =
    mediationCase?.requesting_booking?.berth?.berth_number ||
    originalBerthNumber;
  const conflictingBerthNumber =
    mediationCase?.conflicting_booking?.berth?.berth_number ||
    originalBerthNumber;
  const requestingBookingRange =
    mediationCase?.requesting_booking?.start_date &&
    mediationCase?.requesting_booking?.end_date
      ? `${formatDisplayDateTime(mediationCase.requesting_booking.start_date)} - ${formatDisplayDateTime(mediationCase.requesting_booking.end_date)}`
      : '—';
  const conflictingBookingRange =
    mediationCase?.conflicting_booking?.start_date &&
    mediationCase?.conflicting_booking?.end_date
      ? `${formatDisplayDateTime(mediationCase.conflicting_booking.start_date)} - ${formatDisplayDateTime(mediationCase.conflicting_booking.end_date)}`
      : '—';
  const requestedEndDate = formatDisplayDateTime(
    mediationCase?.requested_end_date
  );

  if (isLoading || !mediationCase) {
    return (
      <div className="card border border-orange-200 dark:border-orange-800 contrast:border-orange-400 bg-orange-50/50 dark:bg-orange-950/40 contrast:bg-black p-4 mb-4">
        <p className="text-sm text-gray-600">Loading mediation details...</p>
      </div>
    );
  }

  const comparableBerths = comparableData?.data ?? [];

  const handlePropose = async () => {
    if (!proposedBerthId) {
      enqueueSnackbar(`Select a berth for ${conflictingBoaterName}`, {
        variant: 'warning',
      });
      return;
    }
    try {
      await proposeReallocation({
        caseId: mediationCaseId,
        proposed_berth_id: proposedBerthId,
        reason: reason || undefined,
      }).unwrap();
      enqueueSnackbar(`Reallocation request sent to ${conflictingBoaterName}`, {
        variant: 'success',
      });
      refetch();
      onResolved?.();
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to propose reallocation'), {
        variant: 'error',
      });
    }
  };

  const handleDeclineExtension = async () => {
    try {
      await resolveExtension({
        caseId: mediationCaseId,
        resolution_type: 'decline_extension',
        reason: reason || undefined,
      }).unwrap();
      enqueueSnackbar(`Extension declined for ${requestingBoaterName}`, {
        variant: 'success',
      });
      refetch();
      onResolved?.();
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to decline extension'), {
        variant: 'error',
      });
    }
  };

  const handleOfferBoaterA = async () => {
    if (!boaterABerthId) {
      enqueueSnackbar(
        `Select an alternative berth for ${requestingBoaterName}`,
        {
          variant: 'warning',
        }
      );
      return;
    }
    try {
      await resolveExtension({
        caseId: mediationCaseId,
        resolution_type: 'offer_alternative',
        boater_a_proposed_berth_id: boaterABerthId,
        reason: reason || undefined,
      }).unwrap();
      enqueueSnackbar(`Alternative berth offered to ${requestingBoaterName}`, {
        variant: 'success',
      });
      refetch();
      onResolved?.();
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to offer alternative'), {
        variant: 'error',
      });
    }
  };

  return (
    <div className="card border border-orange-300 dark:border-orange-800 contrast:border-orange-400 bg-orange-50 dark:bg-orange-950/40 contrast:bg-black p-4 mb-6 shadow-sm">
      <div className="flex items-start gap-3 mb-4">
        <AlertCircle
          className="text-orange-600 dark:text-orange-400 contrast:text-orange-300 shrink-0 mt-0.5"
          size={22}
        />
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Berth extension conflict
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            <span className="font-medium text-gray-800">{statusLabel}</span>
            {mediationCase.harbor?.name && (
              <span className="ml-2 inline-flex items-center gap-1">
                <MapPin size={14} />
                {mediationCase.harbor.name}
              </span>
            )}
            {mediationCase.response_deadline_at && (
              <span className="ml-2 inline-flex items-center gap-1 text-orange-700 dark:text-orange-300 contrast:text-orange-300">
                <Clock size={14} />
                Deadline:{' '}
                {formatDisplayDateTime(mediationCase.response_deadline_at)}
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 text-sm mb-4">
        <div className="rounded-lg border border-orange-100 dark:border-orange-800 contrast:border-orange-400 bg-white dark:bg-coal-600 contrast:bg-coal-600 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-orange-700 dark:text-orange-300 contrast:text-orange-300">
                Extension requester
              </p>
              <p className="mt-1 font-semibold text-gray-900">
                {requestingBoaterName}
              </p>
            </div>
            <span className="rounded-md bg-orange-100 dark:bg-orange-900 contrast:bg-orange-900 px-2 py-1 text-xs font-medium text-orange-800 dark:text-orange-100 contrast:text-orange-100">
              Berth {requestingBerthNumber}
            </span>
          </div>
          {requestingBoaterEmail && (
            <p className="mt-3 flex items-center gap-2 text-gray-600 break-all">
              <Mail size={14} className="shrink-0" />
              {requestingBoaterEmail}
            </p>
          )}
          <p className="mt-2 flex items-center gap-2 text-gray-600">
            <CalendarDays size={14} className="shrink-0" />
            {requestingBookingRange}
          </p>
          <div className="mt-3 grid gap-1 text-xs text-gray-500">
            <p>Booking: {mediationCase.requesting_booking_id || '—'}</p>
            <p>Requested end date: {requestedEndDate}</p>
          </div>
        </div>
        <div className="rounded-lg border border-red-100 dark:border-red-800 contrast:border-red-400 bg-white dark:bg-coal-600 contrast:bg-coal-600 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300 contrast:text-red-300">
                Conflicting booking
              </p>
              <p className="mt-1 font-semibold text-gray-900">
                {conflictingBoaterName}
              </p>
            </div>
            <span className="rounded-md bg-red-100 dark:bg-red-900 contrast:bg-red-900 px-2 py-1 text-xs font-medium text-red-800 dark:text-red-100 contrast:text-red-100">
              Berth {conflictingBerthNumber}
            </span>
          </div>
          {conflictingBoaterEmail && (
            <p className="mt-3 flex items-center gap-2 text-gray-600 break-all">
              <Mail size={14} className="shrink-0" />
              {conflictingBoaterEmail}
            </p>
          )}
          <p className="mt-2 flex items-center gap-2 text-gray-600">
            <CalendarDays size={14} className="shrink-0" />
            {conflictingBookingRange}
          </p>
          <div className="mt-3 grid gap-1 text-xs text-gray-500">
            <p>Booking: {mediationCase.conflicting_booking_id || '—'}</p>
            <p>Original berth: {originalBerthNumber}</p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
        <div className="rounded-lg border bg-white dark:bg-coal-600 contrast:bg-coal-600 p-3">
          <p className="font-medium text-gray-800">Overlap period</p>
          <p className="text-gray-600">{overlapWindow}</p>
        </div>
        <div className="rounded-lg border bg-white dark:bg-coal-600 contrast:bg-coal-600 p-3">
          <p className="font-medium text-gray-800">
            Proposed berth for {conflictingBoaterName}
          </p>
          <p className="text-gray-600">
            {mediationCase.proposed_berth?.berth_number || 'Not proposed'}
          </p>
        </div>
        <div className="rounded-lg border bg-white dark:bg-coal-600 contrast:bg-coal-600 p-3">
          <p className="font-medium text-gray-800">
            Alternative berth for {requestingBoaterName}
          </p>
          <p className="text-gray-600">
            {mediationCase.boater_a_proposed_berth?.berth_number ||
              'Not proposed'}
          </p>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Reason / notes (optional)
        </label>
        <textarea
          className="textarea textarea-bordered w-full text-sm"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Explain why this berth switch is needed"
        />
      </div>

      {(mediationCase.status === 'OPEN' ||
        mediationCase.status === 'AWAITING_MANAGER') && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comparable berth for {conflictingBoaterName}
            </label>
            <select
              aria-label={`Comparable berth for ${conflictingBoaterName}`}
              className="select select-bordered w-full text-sm"
              value={proposedBerthId}
              onChange={(e) => setProposedBerthId(e.target.value)}
              disabled={loadingBerths}
            >
              <option value="">Select berth</option>
              {comparableBerths.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.berth_number}
                  {b.category?.name ? ` (${b.category.name})` : ''}
                </option>
              ))}
            </select>
          </div>
          {mediationCase.status === 'OPEN' && (
            <Button
              type="button"
              onClick={handlePropose}
              disabled={proposing}
              className="btn btn-sm btn-primary"
            >
              Propose reallocation to {conflictingBoaterName}
            </Button>
          )}
        </div>
      )}

      {mediationCase.status === 'AWAITING_MANAGER' && (
        <div className="mt-4 pt-4 border-t border-orange-200 dark:border-orange-800 contrast:border-orange-400 space-y-4">
          <p className="text-sm text-gray-700">
            {conflictingBoaterName} declined or did not respond. Offer{' '}
            {requestingBoaterName} an alternative or decline the extension.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Alternative berth for {requestingBoaterName} (extension period)
            </label>
            <select
              aria-label={`Alternative berth for ${requestingBoaterName}`}
              className="select select-bordered w-full text-sm"
              value={boaterABerthId}
              onChange={(e) => setBoaterABerthId(e.target.value)}
              disabled={loadingBerths}
            >
              <option value="">Select berth</option>
              {comparableBerths.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.berth_number}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={handleOfferBoaterA}
              disabled={resolving}
              className="btn btn-sm btn-primary"
            >
              Offer alternative to {requestingBoaterName}
            </Button>
          </div>
        </div>
      )}

      {mediationCase.status === 'AWAITING_BOATER_B' && (
        <p className="text-sm text-gray-600">
          Waiting for {conflictingBoaterName} to approve or decline the proposed
          berth switch.
        </p>
      )}

      {mediationCase.status === 'AWAITING_BOATER_A' && (
        <p className="text-sm text-gray-600">
          Waiting for {requestingBoaterName} to approve or decline the
          alternative berth for the extension period.
        </p>
      )}

      {mediationCase.status === 'RESOLVED' && (
        <p className="text-sm text-green-700 dark:text-green-300 contrast:text-green-300">
          Mediation resolved. Resolution:{' '}
          {mediationCase.resolution || 'Completed'}.
        </p>
      )}

      {mediationCase.status === 'EXPIRED' && (
        <p className="text-sm text-orange-700 dark:text-orange-300 contrast:text-orange-300">
          The response deadline expired and the case now needs manual follow-up.
        </p>
      )}

      {mediationCase.status === 'CANCELLED' && (
        <p className="text-sm text-gray-600">
          This mediation case was cancelled.
        </p>
      )}

      {mediationCase.status !== 'RESOLVED' &&
        mediationCase.status !== 'CANCELLED' && (
          <div className="mt-4 pt-4 border-t border-orange-200 dark:border-orange-800 contrast:border-orange-400">
            <Button
              type="button"
              onClick={handleDeclineExtension}
              disabled={resolving}
              className="btn btn-sm btn-light"
            >
              Decline extension
            </Button>
          </div>
        )}
    </div>
  );
};

export default BerthMediationPanel;
